// src/pages/Trend/components/analysis/market-trends/BrandTrendChart.js
import React, { memo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import './BrandTrendChart.css'
import { ErrorMessage } from '../../../../../components/common/UI';
import { encodeBrandId } from '../../../../../utils/urlUtils';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';

// Create narrative title based on data insights
const createNarrativeTitle = (primary, secondary, hasSecondaryData, translate) => {
  const topBrand1 = primary.brand_distribution.labels[0];
  const topShare1 = primary.brand_distribution.market_shares[0];
  
  if (!hasSecondaryData) {
    // Single narrative without comparison
    const narrativeText = translate('trendPage.marketTrends.narratives.brand.single.topBrand', {
      brand: topBrand1,
      share: topShare1.toFixed(1)
    });
    
    // Split the narrative to apply styling
    const parts = narrativeText.split(topBrand1);
    const shareParts = parts[1] ? parts[1].split(topShare1.toFixed(1)) : ['', ''];
    
    return (
      <>
        {parts[0]}
        <span style={{ 
          fontWeight: '700',
          color: BBC_STYLE.HIGHLIGHT_COLOR,
          borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}`,
          paddingBottom: '1px'
        }}>
          {topBrand1}
        </span>
        {shareParts[0]}
        <span style={{ 
          fontWeight: '700',
          backgroundColor: 'rgba(250, 171, 24, 0.15)',
          padding: '0 4px',
          borderRadius: '2px'
        }}>
          {topShare1.toFixed(1)}%
        </span>
        {shareParts[1]}
      </>
    );
  }
  
  // Comparison mode
  const topBrand2 = secondary.brand_distribution.labels[0];
  const topShare2 = secondary.brand_distribution.market_shares[0];
  const sameBrand = topBrand1 === topBrand2;
  
  // Combined insight
  const combinedInsight = sameBrand 
    ? translate('trendPage.marketTrends.narratives.brand.comparison.sameBrand', { brand: topBrand1 })
    : translate('trendPage.marketTrends.narratives.brand.comparison.differentBrands');
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      {/* Combined insight first */}
      <div style={{
        fontSize: '1.05rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR
      }}>
        {combinedInsight}
      </div>

      {/* Individual narratives */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        fontSize: '0.85rem'
      }}>
        {/* Primary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set1')}:{' '}
            <span style={{ 
              fontWeight: '700',
              color: BBC_STYLE.PRIMARY_COLOR
            }}>
              {topBrand1}
            </span>
            {' '}({topShare1.toFixed(1)}%)
          </span>
        </div>
        
        {/* Secondary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.SECONDARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set2')}:{' '}
            <span style={{ 
              fontWeight: '700',
              color: BBC_STYLE.SECONDARY_COLOR
            }}>
              {topBrand2}
            </span>
            {' '}({topShare2.toFixed(1)}%)
          </span>
        </div>
        
        {/* Share difference indicator */}
        {Math.abs(topShare1 - topShare2) > 5 && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            backgroundColor: 'rgba(213, 94, 0, 0.08)',
            borderRadius: '4px',
            fontSize: '0.85rem'
          }}>
            <span style={{ 
              fontWeight: '600',
              color: BBC_STYLE.HIGHLIGHT_COLOR
            }}>
              {translate('trendPage.marketTrends.narratives.brand.comparison.shareDiff', { 
                diff: Math.abs(topShare1 - topShare2).toFixed(1) 
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Create subtitle with key insights
const createSubtitle = (primary, secondary, hasSecondaryData, translate) => {
  const totalBrands1 = primary.total_brands;
  const topSharesSum1 = primary.brand_distribution.market_shares.slice(0, 3).reduce((a, b) => a + b, 0);
  const concentrationRatio1 = primary.concentration_ratio;
  
  if (!hasSecondaryData) {
    // Single subtitle without comparison
    const narrativeText = translate('trendPage.marketTrends.narratives.brand.single.topBrandsShare', {
      total: totalBrands1,
      share: topSharesSum1.toFixed(1)
    });
    
    // Split the text to apply styling
    const parts = narrativeText.split(totalBrands1.toString());
    const shareParts = parts[1] ? parts[1].split(topSharesSum1.toFixed(1)) : ['', ''];
    
    return (
      <>
        {parts[0]}
        <span style={{ 
          fontWeight: '600',
          fontSize: '1.05rem',
          color: BBC_STYLE.PRIMARY_COLOR
        }}>
          {totalBrands1}
        </span>
        {shareParts[0]}
        <span style={{ 
          fontWeight: '600',
          color: '#D55E00',
          backgroundColor: 'rgba(213, 94, 0, 0.08)',
          padding: '0 4px',
          borderRadius: '2px'
        }}>
          {topSharesSum1.toFixed(1)}%
        </span>
        {shareParts[1]}
        {concentrationRatio1 > 50 && (
          <>
            <span style={{ 
              margin: '0 10px',
              color: '#D0D0D0'
            }}>｜</span>
            <span style={{ fontWeight: '600' }}>
              {translate('trendPage.marketTrends.narratives.brand.single.marketConcentration', {
                ratio: concentrationRatio1.toFixed(1)
              })}
            </span>
          </>
        )}
      </>
    );
  }
  
  // Comparison mode - single line format
  const totalBrands2 = secondary.total_brands;
  const topSharesSum2 = secondary.brand_distribution.market_shares.slice(0, 3).reduce((a, b) => a + b, 0);
  const concentrationDiff = Math.abs(topSharesSum1 - topSharesSum2);
  
  const set1Summary = translate('trendPage.marketTrends.narratives.brand.comparison.set1Summary', {
    count: totalBrands1,
    share: topSharesSum1.toFixed(1)
  });
  
  const set2Summary = translate('trendPage.marketTrends.narratives.brand.comparison.set2Summary', {
    count: totalBrands2,
    share: topSharesSum2.toFixed(1)
  });
  
  return (
    <>
      {set1Summary.split(totalBrands1.toString())[0]}
      <span style={{ 
        fontWeight: '700',
        fontSize: '1.15rem',
        color: BBC_STYLE.PRIMARY_COLOR
      }}>
        {totalBrands1}
      </span>
      {set1Summary.split(totalBrands1.toString())[1].split(topSharesSum1.toFixed(1))[0]}
      <span style={{ 
        fontWeight: '600',
        backgroundColor: 'rgba(19, 128, 161, 0.08)',
        padding: '2px 6px',
        borderRadius: '3px'
      }}>
        {topSharesSum1.toFixed(1)}%
      </span>
      {set1Summary.split(topSharesSum1.toFixed(1))[1]}
      <span style={{ 
        margin: '0 10px',
        color: '#D0D0D0',
        fontWeight: '300'
      }}>｜</span>
      {set2Summary.split(totalBrands2.toString())[0]}
      <span style={{ 
        fontWeight: '700',
        fontSize: '1.15rem',
        color: BBC_STYLE.SECONDARY_COLOR
      }}>
        {totalBrands2}
      </span>
      {set2Summary.split(totalBrands2.toString())[1].split(topSharesSum2.toFixed(1))[0]}
      <span style={{ 
        fontWeight: '600',
        backgroundColor: 'rgba(250, 171, 24, 0.08)',
        padding: '2px 6px',
        borderRadius: '3px'
      }}>
        {topSharesSum2.toFixed(1)}%
      </span>
      {set2Summary.split(topSharesSum2.toFixed(1))[1]}
      {concentrationDiff > 5 && (
        <>
          <span style={{ 
            margin: '0 10px',
            color: '#D0D0D0',
            fontWeight: '300'
          }}>｜</span>
          <span style={{ 
            fontWeight: '600',
            fontSize: '0.9rem',
            color: BBC_STYLE.HIGHLIGHT_COLOR,
            backgroundColor: 'rgba(213, 94, 0, 0.08)',
            padding: '2px 6px',
            borderRadius: '3px'
          }}>
            {translate('trendPage.marketTrends.narratives.brand.comparison.shareDiff', {
              diff: concentrationDiff.toFixed(1)
            })}
          </span>
        </>
      )}
    </>
  );
};

const BrandTrendChart = ({ brandData }) => {
  const { translate } = useLanguage();

  if (!brandData?.primary?.brand_analysis) {
    return <ErrorMessage message={translate('trendPage.marketTrends.errors.noBrandData')} />;
  }

  const hasSecondaryData = brandData.secondary?.brand_analysis !== undefined;
  const primaryData = brandData.primary.brand_analysis;
  const secondaryData = hasSecondaryData ? brandData.secondary.brand_analysis : null;

  const BrandTable = ({ data, isSecondary }) => {
    const maxShare = Math.max(...data.brand_distribution.market_shares);
    const getScaledWidth = (share) => (share / maxShare) * 45;

    const handleBrandClick = (brandId) => {
      const encodedId = encodeBrandId(brandId);
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}/brand/${encodedId}`, '_blank');
    };

    return (
      <div className={`brand-table-container ${hasSecondaryData ? 'half-width' : 'full-width'}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {hasSecondaryData && (
          <div style={{
            fontSize: '0.85rem',
            color: BBC_STYLE.TEXT_COLOR,
            marginBottom: '10px',
            fontWeight: '600',
            borderLeft: `3px solid ${isSecondary ? BBC_STYLE.SECONDARY_COLOR : BBC_STYLE.PRIMARY_COLOR}`,
            paddingLeft: '10px'
          }}>
            {isSecondary ? translate('trendPage.filters.set2') : translate('trendPage.filters.set1')}
          </div>
        )}

        <div className="brand-table-wrapper" style={{ flex: 1, overflowY: 'auto' }}>
          <table className="brand-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>{translate('trendPage.marketTrends.narratives.brand.single.rank')}</th>
                <th>{translate('trendPage.marketTrends.brandTable.brand')}</th>
                <th style={{ width: '100px' }}>{translate('trendPage.marketTrends.brandTable.products')}</th>
                <th style={{ width: '200px' }}>{translate('trendPage.marketTrends.brandTable.marketShare')}</th>
              </tr>
            </thead>
            <tbody>
              {data.brand_distribution.labels.map((brand, index) => {
                return (
                  <tr key={index} className="brand-table-row">
                    <td style={{
                      color: '#666666',
                      fontSize: '13px'
                    }}>
                      {index + 1}
                    </td>
                    <td
                      className="brand-name"
                      onClick={() => handleBrandClick(data.brand_distribution.brand_ids[index])}
                    >
                      <div className='brand-name-text'>{brand}</div>
                    </td>
                    <td className="product-count" style={{
                      fontWeight: '400',
                      color: '#666666'
                    }}>
                      {data.brand_distribution.values[index]}
                    </td>
                    <td className="market-share">
                      <div className="share-bar-container">
                        <span className="share-percentage">
                          {data.brand_distribution.market_shares[index].toFixed(1)}%
                        </span>
                        <div className="share-bar-wrapper">
                          <div
                            className={`share-bar`}
                            style={{
                              width: `${getScaledWidth(data.brand_distribution.market_shares[index])}%`,
                              backgroundColor: isSecondary ? BBC_STYLE.SECONDARY_COLOR : BBC_STYLE.PRIMARY_COLOR
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <ChartContainer>
      {/* Title and subtitle with padding */}
      <div style={{ padding: '0 0 8px 0' }}>
        {/* Narrative Title with visual effects */}
        <h2 style={{
          fontSize: '1.05rem',
          fontWeight: '600',
          color: BBC_STYLE.TEXT_COLOR,
          margin: '0 0 6px 0',
          lineHeight: '1.3'
        }}>
          {createNarrativeTitle(primaryData, secondaryData, hasSecondaryData, translate)}
        </h2>

        {/* Subtitle with insights and highlighting */}
        <div style={{
          fontSize: '0.8rem',
          color: BBC_STYLE.TEXT_COLOR,
          margin: '0 0 12px 0',
          lineHeight: '1.4'
        }}>
          {createSubtitle(primaryData, secondaryData, hasSecondaryData, translate)}
        </div>
      </div>

      {/* Brand Tables - full width */}
      <div className="brand-analysis-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className={`brand-analysis-container ${hasSecondaryData ? 'has-secondary' : ''}`} style={{ flex: 1, overflow: 'hidden' }}>
          <BrandTable data={primaryData} isSecondary={false} />
          {hasSecondaryData && (
            <BrandTable data={secondaryData} isSecondary={true} />
          )}
        </div>
      </div>
    </ChartContainer>
  );
};

export default memo(BrandTrendChart);
